<script setup lang="ts">
import type { ResolvedRange } from '@/lib/charts/timeRange'
import type { ChartSeries } from '@/lib/charts/types'
import type { Customer, FleetSeriesData, FleetTimelineRow } from '@/types'
import { computed } from 'vue'
import ChartPanel from '@/components/charts/ChartPanel.vue'
import TimeSeriesChart from '@/components/charts/TimeSeriesChart.vue'
import StatusTimeline from '@/components/telemetry/StatusTimeline.vue'

interface TimelineInstance {
  id: number
  identifier: string | null
  customer: Pick<Customer, 'name'>
}

const props = defineProps<{
  series: FleetSeriesData
  timeline: FleetTimelineRow[]
  instances: TimelineInstance[]
  resolved: ResolvedRange
  loading?: boolean
}>()

const VERSION_PALETTE = [
  'var(--ui-primary)',
  '#f59e0b',
  '#10b981',
  '#6366f1',
  '#ec4899',
  '#14b8a6',
  '#94a3b8',
]

const versionSeries = computed<ChartSeries[]>(() =>
  props.series.versions.map((band, index) => ({
    id: band.key,
    label: versionLabel(band.key),
    color: VERSION_PALETTE[index % VERSION_PALETTE.length],
    type: 'area',
    points: band.points,
  })),
)

function versionLabel(key: string): string {
  if (key === 'other')
    return 'Other'
  if (key === 'unknown')
    return 'Unknown'
  return key
}

function hasData(series: ChartSeries[]): boolean {
  return series.some(entry => entry.points.some(point => (point.y ?? 0) > 0))
}

function latestValue(series: ChartSeries): number {
  for (let index = series.points.length - 1; index >= 0; index--) {
    const value = series.points[index].y
    if (value != null)
      return value
  }
  return 0
}

const formatCount = (value: number) => Math.round(value).toString()
</script>

<template>
  <div class="flex flex-col gap-8">
    <ChartPanel
      title="Health status over time"
      subtitle="Each instance's real status history — segment edges are actual heartbeats, never interpolated."
      :loading="loading"
      :empty="timeline.length === 0"
      empty-text="No instances in this range."
    >
      <StatusTimeline :timeline="timeline" :instances="instances" :resolved="resolved" />
    </ChartPanel>

    <ChartPanel
      title="Version adoption over time"
      subtitle="How the fleet shifted across versions."
      :loading="loading"
      :empty="!hasData(versionSeries)"
      empty-text="No heartbeats in this range."
    >
      <TimeSeriesChart :series="versionSeries" :resolved="resolved" stacked :height="220" :value-format="formatCount" />
      <ul class="mt-4 flex flex-wrap gap-x-4 gap-y-2">
        <li v-for="entry in versionSeries" :key="entry.id" class="flex items-center gap-2 text-sm">
          <span class="h-0.5 w-3.5 rounded-full" :style="{ backgroundColor: entry.color }" />
          <span class="text-toned font-mono">{{ entry.label }}</span>
          <span class="text-muted text-xs tabular-nums">{{ latestValue(entry) }}</span>
        </li>
      </ul>
    </ChartPanel>
  </div>
</template>
